import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FieldTypeEnum } from "src/enums/fieldTypeEnum";
import { genericEntityService } from "src/services/genericEntityService";
import { ItemForm } from "./ItemForm";
import { useErrorHandler } from "../shared/errorHandler";

/**
 * ExtensionForm Component
 *
 * @param {string} props.extendedEntityId - The ID of the extended entity.
 * @param {string} props.extendedItemId - The ID of the extended item.
 * @param {boolean} props.canEdit - Whether the form allows editing.
 * @param {function} props.handleErrorCallback - A callback function to handle errors.
 */
export const ExtensionForm = forwardRef(({ extendedEntityId, extendedItemId, canEdit, handleErrorCallback }, ref) => {

    useImperativeHandle(ref, () => {
        return {
            handleCreate: handleCreate,
            canSave: canSave,
            handleSave: handleSave
        }
    })

    const { handleHttpError } = useErrorHandler();

    const [entity, setEntity] = useState(null);
    const [itemId, setItemId] = useState(null);
    const [itemForm, setItemForm] = useState({
        form: {},
        errors: {}
    });

    const reportError = (error) => {
        if (handleErrorCallback) {
            handleErrorCallback(error)
        } else {
            handleHttpError(error);
        }; 
    };


    useEffect(() => {
        if (!extendedEntityId) {
            return;
        }

        genericEntityService.getExtensionEntity(extendedEntityId, extendedItemId).then((response) => {
            setEntity(response.data.Entity);

            if (!response.data.Item){
                return; 
            }
            
            setItemId(response.data.Item?.ItemId);

            let values = {};
            const fields = response.data.Item.Fields;
            for (const key in fields) {
                if (fields[key] === null) {
                    continue;
                }

                var value;
                switch (fields[key].type) {
                    case FieldTypeEnum.Boolean:
                        value = "false";
                        break;
                    case FieldTypeEnum.Attachment:
                        continue;

                    default:
                        value = fields[key];
                        break;
                }
                values[key] = value;
            }
            setItemForm({
                ...itemForm,
                form: {
                    ...values,
                    ...response.data.attachments
                }
            });
        }).catch((error) => {
            reportError(error);
        })
    }, [extendedEntityId, extendedItemId])

    const handleFormChange = (form) => setItemForm(form);

    /**
     * Create a new item.
     *
     * @param {string} id - The ID of the extended entity to which the new item should be associated.
     */
    const handleCreate = (id) => {
        genericEntityService.createItem(entity.EntityId, {
            ...itemForm.form,
            extendedEntityId: id
        }).catch((error) => {
            reportError(error);
        })
    }

    /**
     * Handle form change.
     *
     * @param {Object} form - The updated form data.
     */
    const handleSave = () => {
        if (!itemId) {
            // Se não existe itemId mas temos extendedItemId (GUID), usar esse GUID
            const guidToUse = extendedItemId || extendedEntityId;       
            genericEntityService.createItem(entity?.EntityId, {
                ...itemForm.form,
                extendedEntityId: guidToUse
            }).catch((error) => {
                reportError(error);
            })
        } else {
            genericEntityService.updateItemById(entity?.EntityId, itemId, itemForm.form)
                .catch((error) => {
                    reportError(error);
                })
        }
    }

    /**
     * Check if the form data is valid.
     *
     * @returns {boolean} - True if the form is valid, false otherwise.
     */
    const canSave = () => {
        // Verificar se entity foi carregado (API retorna Fields com F maiúsculo)
        if (!entity || !entity.Fields) {
            return false;
        }
        if (Object.values(itemForm.errors).some(value => value !== null && value !== undefined)) {
            return false;
        }

        for (const field of entity.Fields) {
            if (field.IsMandatory &&
                (itemForm.form[field.Code] === "" ||
                    itemForm.form[field.Code] === null || itemForm.form[field.Code] === undefined)) {
                return false;
            }
        }

        return true;
    }

    return (
        <>
            {entity &&
                <ItemForm
                    entity={entity}
                    itemId={null}
                    itemForm={itemForm}
                    changeFormCallback={handleFormChange}
                    canEdit={canEdit}
                />
            }
        </>
    )
})